import type { ClientOverview, Currency } from '../../types/api';
import { convertUSDToCurrency, convertUSDToCurrencyFormatted } from './currencyConversion';

/**
 * Get total cost (assets value) for a specific unit from client overview
 * @param clientOverviewData - Array of ClientOverview data
 * @param unitIdNo - The unit ID number to filter by
 * @returns Total cost (assets value) for the specified unit
 */
export function getTotalCostForUnit(
  clientOverviewData: ClientOverview[],
  unitIdNo: number
): number {
  if (!clientOverviewData || clientOverviewData.length === 0 || !unitIdNo) {
    return 0;
  }

  const unitOverview = clientOverviewData.find(
    (overview) => overview.unit_id_no === unitIdNo
  );

  return unitOverview?.total_assets_usd || 0;
}

/**
 * Get total cost (assets value) for a specific unit with formatting
 * @param clientOverviewData - Array of ClientOverview data
 * @param unitIdNo - The unit ID number to filter by
 * @returns Formatted total cost string
 */
export function getTotalCostForUnitFormatted(
  clientOverviewData: ClientOverview[],
  unitIdNo: number
): string {
  const total = getTotalCostForUnit(clientOverviewData, unitIdNo);
  
  if (total === 0) {
    return '0';
  }
  
  // Format with commas for thousands
  return total.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
}

/**
 * Get total cost in a specific currency for a specific unit
 * @param clientOverviewData - Array of ClientOverview data
 * @param unitIdNo - The unit ID number to filter by
 * @param targetCurrencyCode - Target currency code
 * @param currencies - Array of currency data
 * @returns Total cost value in target currency
 */
export function getTotalCostForUnitInCurrency(
  clientOverviewData: ClientOverview[],
  unitIdNo: number,
  targetCurrencyCode: string,
  currencies: Currency[]
): number {
  const totalUSD = getTotalCostForUnit(clientOverviewData, unitIdNo);
  return convertUSDToCurrency(totalUSD, targetCurrencyCode, currencies);
}

/**
 * Get total cost in a specific currency for a specific unit with formatting
 * @param clientOverviewData - Array of ClientOverview data
 * @param unitIdNo - The unit ID number to filter by
 * @param targetCurrencyCode - Target currency code
 * @param currencies - Array of currency data
 * @returns Formatted total cost string in target currency
 */
export function getTotalCostForUnitInCurrencyFormatted(
  clientOverviewData: ClientOverview[],
  unitIdNo: number,
  targetCurrencyCode: string,
  currencies: Currency[]
): string {
  const totalUSD = getTotalCostForUnit(clientOverviewData, unitIdNo);
  return convertUSDToCurrencyFormatted(totalUSD, targetCurrencyCode, currencies, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useCurrencySymbol: false
  });
}

